import { supabase } from '../lib/supabase';

/**
 * Generate a unique permalink for assessment results
 * Format: strategic-profile-{random-string}
 */
export const generateResultsPermalink = () => {
  const randomString = Math.random().toString(36).substr(2, 8);
  return `strategic-profile-${randomString}`;
};

/**
 * Save formatted assessment results to database
 */
export const saveFormattedResults = async (userId, insights, formattedHtml) => {
  try {
    const permalink = generateResultsPermalink();
    
    const { data, error } = await supabase
      .from('assessment_responses')
      .update({
        formatted_results_html: formattedHtml,
        results_permalink: permalink,
        last_viewed_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('assessment_type', 'enhanced')
      .order('completed_at', { ascending: false })
      .limit(1)
      .select()
      .single();

    if (error) {
      console.error('Error saving formatted results:', error);
      
      // Check if this is an authentication error
      if (error.code === 'PGRST301' || error.message?.includes('JWT') || error.message?.includes('auth')) {
        console.warn('Authentication error detected in saveFormattedResults - user may need to re-login');
      }
      
      return null;
    }

    console.log('✅ Formatted results saved with permalink:', permalink);
    return { ...data, results_permalink: permalink };
    
  } catch (error) {
    console.error('Error in saveFormattedResults:', error);
    
    // Check if this is an authentication error
    if (error.message?.includes('JWT') || error.message?.includes('auth') || error.message?.includes('session')) {
      console.warn('Authentication error caught in saveFormattedResults - user may need to re-login');
    }
    
    return null;
  }
};

/**
 * Load assessment results by permalink
 */
export const loadResultsByPermalink = async (permalink) => {
  try {
    const { data, error } = await supabase
      .from('assessment_responses')
      .select('*')
      .eq('results_permalink', permalink)
      .single();

    if (error) {
      console.error('Error loading results by permalink:', error);
      return null;
    }

    // Increment view count
    await supabase
      .from('assessment_responses')
      .update({ 
        results_view_count: (data.results_view_count || 0) + 1,
        last_viewed_at: new Date().toISOString()
      })
      .eq('id', data.id);

    return data;
    
  } catch (error) {
    console.error('Error in loadResultsByPermalink:', error);
    return null;
  }
};

/**
 * Get results history for a user
 */
export const getUserResultsHistory = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('assessment_responses')
      .select('id, completed_at, results_permalink, results_view_count, last_viewed_at')
      .eq('user_id', userId)
      .eq('assessment_type', 'enhanced')
      .not('completed_at', 'is', null)
      .order('completed_at', { ascending: false });

    if (error) {
      console.error('Error loading user results history:', error);
      return [];
    }

    return data || [];
    
  } catch (error) {
    console.error('Error in getUserResultsHistory:', error);
    return [];
  }
};

/**
 * Convert React component to HTML string for storage
 */
export const renderResultsToHtml = (insights, currentUser, studentGradeLevel) => {
  // Handle both formatted string and structured object formats
  if (typeof insights === 'string') {
    // If insights is already a formatted string, wrap it in basic HTML
    const studentName = currentUser?.displayName || currentUser?.name || 'Student';
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${studentName}'s Strategic Profile - PaperSedge</title>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; margin: 0; padding: 20px; background: #fafaf9; }
        .container { max-width: 1200px; margin: 0 auto; }
        .content { background: white; padding: 2rem; border-radius: 1.5rem; box-shadow: 0 4px 15px rgba(0,0,0,0.1); }
        h1, h2, h3 { color: #1e293b; }
        h1 { font-size: 2.5rem; text-align: center; margin-bottom: 2rem; }
        h2 { font-size: 1.8rem; margin-top: 2rem; margin-bottom: 1rem; }
        h3 { font-size: 1.3rem; margin-top: 1.5rem; margin-bottom: 0.5rem; }
        p { color: #374151; }
        ul, ol { color: #374151; }
        .footer { text-align: center; margin: 3rem 0; color: #64748b; }
    </style>
</head>
<body>
    <div class="container">
        <div class="content">
            ${insights.replace(/\n/g, '<br>')}
        </div>
        <div class="footer">
            <p>Generated by PaperSedge Strategic Assessment • ${new Date().toLocaleDateString()}</p>
            <p style="font-size: 0.875rem;">This personalized profile is based on your unique responses and strategic analysis.</p>
        </div>
    </div>
</body>
</html>`;
  }
  
  // Handle nested structure from ConversationalResults component
  // The insights object may come in two formats:
  // 1. Nested: { studentProfile: {...}, insights: { strategicAdvantage: {...}, ... } }
  // 2. Flat: { strategicAdvantage: {...}, intellectualSignature: {...}, ... }
  
  let insightsData = insights;
  let studentProfile = null;
  
  // Check if we have the nested structure
  if (insights && typeof insights === 'object' && insights.insights) {
    // Extract the actual insights data from the nested structure
    insightsData = insights.insights;
    studentProfile = insights.studentProfile;
  }
  
  // Ensure we have valid data before proceeding
  if (!insightsData || typeof insightsData !== 'object') {
    console.error('Invalid insights data structure:', insights);
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Strategic Profile - Error</title>
</head>
<body>
    <div style="text-align: center; padding: 2rem;">
        <h1>Error Generating Profile</h1>
        <p>Unable to generate your strategic profile. Please try again.</p>
    </div>
</body>
</html>`;
  }
  
  // Legacy structured format handling
  const studentName = studentProfile?.name || currentUser?.displayName || currentUser?.name || 'Student';
  
  return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${studentName}'s Strategic Profile - PaperSedge</title>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; margin: 0; padding: 20px; background: #fafaf9; }
        .container { max-width: 1200px; margin: 0 auto; }
        .header { text-align: center; background: white; padding: 3rem; border-radius: 1.5rem; margin-bottom: 2rem; box-shadow: 0 10px 25px rgba(0,0,0,0.1); }
        .title { font-size: 2.5rem; font-weight: 800; color: #1e293b; margin-bottom: 1rem; }
        .strategic-advantage { background: linear-gradient(135deg, #fef3c7, #fbbf24); padding: 1.5rem; border-radius: 1rem; margin: 2rem 0; border: 2px solid #f59e0b; }
        .card { background: white; padding: 2rem; border-radius: 1.5rem; margin: 1.5rem 0; box-shadow: 0 4px 15px rgba(0,0,0,0.1); }
        .card-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; }
        .card-icon { width: 3rem; height: 3rem; border-radius: 0.75rem; display: flex; align-items: center; justify-content: center; font-size: 1.25rem; color: white; }
        .card-title { font-size: 1.5rem; font-weight: 700; color: #1e293b; }
        .card-subtitle { color: #64748b; font-size: 0.875rem; }
        .content { color: #1e293b; line-height: 1.7; }
        .list { margin: 1rem 0; }
        .list-item { margin: 0.5rem 0; display: flex; align-items: flex-start; gap: 0.5rem; }
        .list-bullet { color: #0d9488; font-weight: bold; }
        .highlight-box { background: #f8fafc; border-left: 4px solid #0d9488; padding: 1rem; border-radius: 0.5rem; margin: 1rem 0; }
        .colleges-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 2rem; margin: 2rem 0; }
        .college-list { background: #f8fafc; padding: 1.5rem; border-radius: 1rem; border: 1px solid #e2e8f0; }
        .college-list h3 { color: #1e293b; margin-bottom: 1rem; display: flex; align-items: center; gap: 0.5rem; }
        .action-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 1.5rem; margin: 2rem 0; }
        .action-item { background: #f8fafc; padding: 1.5rem; border-radius: 1rem; border: 1px solid #e2e8f0; }
        .action-step { background: linear-gradient(135deg, #0d9488, #0f766e); color: white; width: 2rem; height: 2rem; border-radius: 0.5rem; display: flex; align-items: center; justify-content: center; font-weight: bold; margin-bottom: 1rem; }
        .footer { text-align: center; margin: 3rem 0; color: #64748b; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="title">${studentName}'s Strategic Profile</div>
            <p style="color: #64748b; font-size: 1.25rem; margin: 0;">Here's what makes you unique and how to leverage it for college admissions</p>
            
            <div class="strategic-advantage">
                <div style="font-weight: 700; color: #92400e; margin-bottom: 0.75rem; text-transform: uppercase; font-size: 0.875rem; letter-spacing: 0.05em;">Your Strategic Advantage</div>
                <div style="font-size: 1.125rem; font-weight: 600; color: #1e293b;">${insightsData.strategicAdvantage?.primaryAdvantage || insightsData.strategicAdvantage || 'Your unique combination of skills makes you stand out'}</div>
            </div>
        </div>

        <div class="card">
            <div class="card-header">
                <div class="card-icon" style="background: linear-gradient(135deg, #0d9488, #0f766e);">🧮</div>
                <div>
                    <div class="card-title">${insightsData.intellectualSignature?.title || 'How Your Brain Works'}</div>
                    <div class="card-subtitle">${insightsData.intellectualSignature?.subtitle || 'Your unique thinking style'}</div>
                </div>
            </div>
            <div class="content">
                <p>${insightsData.intellectualSignature?.content || ''}</p>
                <div class="list">
                    ${Array.isArray(insightsData.intellectualSignature?.leveragePoints) 
                        ? insightsData.intellectualSignature.leveragePoints.map(point => `
                            <div class="list-item">
                                <span class="list-bullet">→</span>
                                <span>${point}</span>
                            </div>
                        `).join('')
                        : ''}
                </div>
                ${insightsData.intellectualSignature?.strategicAngle ? `
                <div class="highlight-box">
                    <strong>🎯 Your angle:</strong> ${insightsData.intellectualSignature.strategicAngle}
                </div>
                ` : ''}
            </div>
        </div>

        <div class="card">
            <div class="card-header">
                <div class="card-icon" style="background: linear-gradient(135deg, #ec4899, #be185d);">⚡</div>
                <div>
                    <div class="card-title">${insightsData.hiddenSuperpowers?.title || 'Your Secret Weapons'}</div>
                    <div class="card-subtitle">${insightsData.hiddenSuperpowers?.subtitle || 'The unique strengths that set you apart'}</div>
                </div>
            </div>
            <div class="content">
                <p>${insightsData.hiddenSuperpowers?.content || ''}</p>
                <div class="list">
                    ${Array.isArray(insightsData.hiddenSuperpowers?.superpowers) 
                        ? insightsData.hiddenSuperpowers.superpowers.map(power => {
                            // Handle both string and object formats
                            const powerText = typeof power === 'object' ? 
                                `${power.power}: ${power.applicationStrategy || power.uniqueValue || ''}` : 
                                power;
                            return `
                            <div class="list-item">
                                <span class="list-bullet">→</span>
                                <span>${powerText}</span>
                            </div>
                            `;
                        }).join('')
                        : ''}
                </div>
                ${insightsData.hiddenSuperpowers?.narrative ? `
                <div class="highlight-box">
                    <strong>🎯 Your narrative:</strong> ${insightsData.hiddenSuperpowers.narrative}
                </div>
                ` : ''}
            </div>
        </div>

        <div class="card">
            <div class="card-header">
                <div class="card-icon" style="background: linear-gradient(135deg, #f59e0b, #d97706);">🎯</div>
                <div>
                    <div class="card-title">${insightsData.strategicPositioning?.title || 'How to Stand Out'}</div>
                    <div class="card-subtitle">${insightsData.strategicPositioning?.subtitle || 'Your competitive advantage'}</div>
                </div>
            </div>
            <div class="content">
                ${insightsData.strategicPositioning?.brand ? `<p><strong>Your brand:</strong> ${insightsData.strategicPositioning.brand}</p>` : ''}
                ${insightsData.strategicPositioning?.whyItWorks ? `<p><strong>Why this works:</strong> ${insightsData.strategicPositioning.whyItWorks}</p>` : ''}
                <div class="list">
                    ${Array.isArray(insightsData.strategicPositioning?.deployment) 
                        ? insightsData.strategicPositioning.deployment.map(item => {
                            // Handle both string and object formats
                            const itemText = typeof item === 'object' ?
                                `${item.component}: ${item.strategy}` :
                                item;
                            return `
                            <div class="list-item">
                                <span class="list-bullet">→</span>
                                <span>${itemText}</span>
                            </div>
                            `;
                        }).join('')
                        : ''}
                </div>
                ${insightsData.strategicPositioning?.pitch ? `
                <div class="highlight-box">
                    <strong>🎯 Your pitch:</strong> ${insightsData.strategicPositioning.pitch}
                </div>
                ` : ''}
            </div>
        </div>

        ${insightsData.essayStrategy ? `
        <div class="card">
            <div class="card-header">
                <div class="card-icon" style="background: linear-gradient(135deg, #dc2626, #b91c1c);">✍️</div>
                <div>
                    <div class="card-title">${insightsData.essayStrategy.title || 'Essay Game Plan'}</div>
                    <div class="card-subtitle">${insightsData.essayStrategy.subtitle || 'Your narrative strategy'}</div>
                </div>
            </div>
            <div class="content">
                ${insightsData.essayStrategy.goldStory ? `
                <div class="highlight-box">
                    <strong>🏆 ${insightsData.essayStrategy.goldStory.title || 'Your Best Story'}:</strong> ${insightsData.essayStrategy.goldStory.story || ''}
                    ${insightsData.essayStrategy.goldStory.strategicFraming ? `<br><strong>Strategic framing:</strong> ${insightsData.essayStrategy.goldStory.strategicFraming}` : ''}
                </div>
                ` : ''}
                <div class="list">
                    ${Array.isArray(insightsData.essayStrategy?.essayTopics) 
                        ? insightsData.essayStrategy.essayTopics.map(topic => {
                            // Handle both string and object formats
                            const topicText = typeof topic === 'object' ?
                                topic.topic || topic.title || '' :
                                topic;
                            return `
                            <div class="list-item">
                                <span class="list-bullet">→</span>
                                <span>${topicText}</span>
                            </div>
                            `;
                        }).join('')
                        : ''}
                </div>
                ${insightsData.essayStrategy.theme ? `
                <div class="highlight-box">
                    <strong>🎯 Essay theme:</strong> ${insightsData.essayStrategy.theme}
                </div>
                ` : ''}
            </div>
        </div>
        ` : ''}

        ${insightsData.collegeRecommendations ? `
        <div class="card">
            <div class="card-header">
                <div class="card-icon" style="background: linear-gradient(135deg, #3b82f6, #1d4ed8);">🏫</div>
                <div>
                    <div class="card-title">${insightsData.collegeRecommendations.title || 'College Recommendations'}</div>
                    <div class="card-subtitle">${insightsData.collegeRecommendations.subtitle || 'Schools that match your profile'}</div>
                </div>
            </div>
            <div class="colleges-grid">
                ${insightsData.collegeRecommendations.reach && insightsData.collegeRecommendations.reach.length > 0 ? `
                <div class="college-list">
                    <h3>🚀 Reach Schools</h3>
                    ${Array.isArray(insightsData.collegeRecommendations.reach) 
                        ? insightsData.collegeRecommendations.reach.map(school => {
                            // Handle both string and object formats
                            const schoolText = typeof school === 'object' ?
                                school.school || school.name || '' :
                                school;
                            return `<div class="list-item"><span class="list-bullet">•</span><span>${schoolText}</span></div>`;
                        }).join('')
                        : ''}
                </div>
                ` : ''}
                ${insightsData.collegeRecommendations.target && insightsData.collegeRecommendations.target.length > 0 ? `
                <div class="college-list">
                    <h3>🎯 Target Schools</h3>
                    ${Array.isArray(insightsData.collegeRecommendations.target) 
                        ? insightsData.collegeRecommendations.target.map(school => {
                            const schoolText = typeof school === 'object' ?
                                school.school || school.name || '' :
                                school;
                            return `<div class="list-item"><span class="list-bullet">•</span><span>${schoolText}</span></div>`;
                        }).join('')
                        : ''}
                </div>
                ` : ''}
                ${insightsData.collegeRecommendations.safety && insightsData.collegeRecommendations.safety.length > 0 ? `
                <div class="college-list">
                    <h3>✅ Safety Schools</h3>
                    ${Array.isArray(insightsData.collegeRecommendations.safety) 
                        ? insightsData.collegeRecommendations.safety.map(school => {
                            const schoolText = typeof school === 'object' ?
                                school.school || school.name || '' :
                                school;
                            return `<div class="list-item"><span class="list-bullet">•</span><span>${schoolText}</span></div>`;
                        }).join('')
                        : ''}
                </div>
                ` : ''}
            </div>
        </div>
        ` : ''}

        ${insightsData.actionPlan && insightsData.actionPlan.length > 0 ? `
        <div class="card">
            <div class="card-header">
                <div class="card-icon" style="background: linear-gradient(135deg, #0d9488, #0f766e);">📋</div>
                <div>
                    <div class="card-title">Your Next Steps</div>
                    <div class="card-subtitle">Strategic action plan for college admissions</div>
                </div>
            </div>
            <div class="action-grid">
                ${Array.isArray(insightsData.actionPlan) 
                    ? insightsData.actionPlan.map(action => `
                        <div class="action-item">
                            <div class="action-step">${action.step || ''}</div>
                            <h4 style="margin: 0 0 0.5rem 0; color: #1e293b;">${action.title || ''}</h4>
                            <p style="margin: 0 0 1rem 0; color: #64748b; font-size: 0.875rem;">${action.description || ''}</p>
                            ${action.timeline ? `
                            <div style="background: #dcfdf7; border: 1px solid #5eead4; padding: 0.5rem; border-radius: 0.5rem; font-size: 0.75rem; color: #0f766e; font-weight: 600;">
                                ⏱️ ${action.timeline}
                            </div>
                            ` : ''}
                        </div>
                    `).join('')
                    : ''}
            </div>
        </div>
        ` : ''}

        <div class="footer">
            <p>Generated by PaperSedge Strategic Assessment • ${new Date().toLocaleDateString()}</p>
            <p style="font-size: 0.875rem;">This personalized profile is based on your unique responses and strategic analysis.</p>
        </div>
    </div>
</body>
</html>`;
};